import type {
  CreateRecurringGivingRequest,
  DonationFund,
  DonationMethod,
  DonationResponse,
  PaginatedResponse,
  PublicCheckoutRequest,
  PublicCheckoutResponse,
  PublicTenantInfoResponse,
  RecordDonationRequest,
  RecurringGivingScheduleResponse,
  UpdateDonationRequest,
} from '@tabita/contracts';
import { apiRequest } from './api-client';

export interface ListDonationsParams {
  readonly donorPersonId?: string | undefined;
  readonly fund?: DonationFund | undefined;
  readonly method?: DonationMethod | undefined;
  readonly dateFrom?: string | undefined;
  readonly dateTo?: string | undefined;
  readonly limit?: number | undefined;
}

export const givingApi = {
  list(params: ListDonationsParams = {}, signal?: AbortSignal) {
    return apiRequest<PaginatedResponse<DonationResponse>>('/giving/donations', {
      query: { ...params },
      signal,
    });
  },

  record(body: RecordDonationRequest) {
    return apiRequest<DonationResponse>('/giving/donations', { method: 'POST', body });
  },

  update(id: string, body: UpdateDonationRequest) {
    return apiRequest<DonationResponse>(`/giving/donations/${id}`, { method: 'PATCH', body });
  },

  refund(id: string) {
    return apiRequest<DonationResponse>(`/giving/donations/${id}/refund`, { method: 'POST' });
  },
};

/** The public, unauthenticated `/give/:tenantSlug` page's own API surface —
 *  no session, so every call here is `anonymous: true`. */
export const publicGivingApi = {
  getTenantInfo(tenantSlug: string, signal?: AbortSignal) {
    return apiRequest<PublicTenantInfoResponse>(`/giving/public/${tenantSlug}`, {
      anonymous: true,
      signal,
    });
  },

  checkout(tenantSlug: string, body: PublicCheckoutRequest) {
    return apiRequest<PublicCheckoutResponse>(`/giving/public/${tenantSlug}/checkout`, {
      method: 'POST',
      body,
      anonymous: true,
    });
  },

  createRecurring(tenantSlug: string, body: CreateRecurringGivingRequest) {
    return apiRequest<RecurringGivingScheduleResponse>(`/giving/public/${tenantSlug}/recurring`, {
      method: 'POST',
      body,
      anonymous: true,
    });
  },
};

/** The `/give/manage/:token` page's own API surface — a donor managing their
 *  own recurring gift with no account, by the signed link an email carried. */
export const recurringGivingManageApi = {
  get(token: string, signal?: AbortSignal) {
    return apiRequest<RecurringGivingScheduleResponse>(`/giving/recurring/${token}`, {
      anonymous: true,
      signal,
    });
  },

  pause(token: string) {
    return apiRequest<RecurringGivingScheduleResponse>(`/giving/recurring/${token}/pause`, {
      method: 'POST',
      anonymous: true,
    });
  },

  resume(token: string) {
    return apiRequest<RecurringGivingScheduleResponse>(`/giving/recurring/${token}/resume`, {
      method: 'POST',
      anonymous: true,
    });
  },

  cancel(token: string) {
    return apiRequest<RecurringGivingScheduleResponse>(`/giving/recurring/${token}/cancel`, {
      method: 'POST',
      anonymous: true,
    });
  },
};
